import React, {
    type Dispatch,
    type SetStateAction,
    useCallback, useEffect, useMemo, useState,
} from 'react';

import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {FlatButton} from '@pb/kit-component-button';
import {Dropdown} from '@pb/kit-component-dropdown';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import Typography from '@pb/kit-component-typography';

import {FiltersFooter} from './filters-footer';

import {filtersBody, filtersHeader} from './filters.module.styl';

import type {DropdownProps} from '@pb/kit-component-dropdown';


type Props<FiltersType> = {
    children: (filters: FiltersType, setFilters: Dispatch<SetStateAction<FiltersType>>) => React.ReactNode,
    filters: FiltersType,
    defaultFilters: FiltersType,

    onApply: (filters: FiltersType) => void,
    onClear: () => FiltersType,

    disabled?: boolean,
    maxHeight?: number,
    caption?: string,
    getAnchor?: DropdownProps['anchor'],
    width?: number,
};

const DropdownMenuBody: DropdownProps['bodyComponent'] = ({children}) => (<>{children}</>);

export const SectionFilter = <FiltersType, >(props: Props<FiltersType>) => {
    const {
        children,
        defaultFilters,
        caption,
        onApply,
        onClear,
        disabled,
        maxHeight,
        width,
    } = props;

    const [mode, setMode] = useState<undefined | 'apply' | 'clear' | 'open'>(undefined);
    const [filters, setFilters] = useState<FiltersType>(props.filters);

    const getAnchor = useCallback<NonNullable<DropdownProps['anchor']>>(({anchorRef, positionRef, getAnchorProps}) => {
        const count = Object.values(filters as Record<string, unknown>).filter(Boolean).length;
        const filterIconType = count ? Icons.FILTER_FILLED : Icons.FILTER_EMPTY;

        return (
            <FlatButton
                startIcon={<Icon type={filterIconType}/>}
                endIcon={<Icon type={Icons.ANGLE_DOWN} ref={positionRef}/>}
                disabled={disabled}
                ref={anchorRef}
                {...getAnchorProps()}>
                <b>Filters ({count})</b>
            </FlatButton>
        );
    }, [filters, disabled]);

    useEffect(() => {
        setFilters(props.filters);
    }, [props.filters]);

    const applyFilters = useCallback(() => {
        setMode('apply');
    }, [setMode]);

    const resetFilters = useCallback(() => {
        setMode('clear');
    }, [setMode]);

    const setShown = useCallback((shown: boolean) => {
        if (shown) {
            setMode('open');
        } else {
            setMode(undefined);
            onApply(filters as FiltersType);
        }
    }, [setMode, onApply, filters]);

    useEffectWithDeps((prev, current) => {
        if (prev.mode !== current.mode && current.mode) {
            if (current.mode === 'apply') {
                current.onApply(current.filters as FiltersType);
                current.setMode(undefined);
            } else if (current.mode === 'clear') {
                current.setFilters(current.onClear());
                current.setMode(undefined);
            }
        }
    }, {mode, filters, onApply, onClear, setFilters, setMode});

    const disableApply = useMemo(
        () => isDeepEqual({prev: props.filters, current: filters}),
        [props.filters, filters],
    );

    const disableClear = useMemo(
        () => isDeepEqual({prev: defaultFilters, current: filters}),
        [defaultFilters, filters],
    );

    return (
        <Dropdown {...{
            maxHeight,
            anchor: props.getAnchor || getAnchor,
            shown: mode === 'open',
            setShown,
            hideOnBodyLeave: false,
            disabled,
            bodyComponent: DropdownMenuBody,
            maxWidth: width,
        }}>
            <Group direction="v" style={{width: width || 311}}>
                {!!caption && <Typography className={filtersHeader} fontWeight="semi-bold" variant="heading-level-3">{caption}</Typography>}
                <div className={filtersBody}>
                    {children(filters, setFilters)}
                </div>
                <FiltersFooter
                    onClear={resetFilters}
                    onApply={applyFilters}
                    disableApply={disableApply}
                    disableClear={disableClear}
                />
            </Group>
        </Dropdown>
    );
};
